import { useEffect, useMemo, useState } from "react";
import { Download } from "lucide-react";
import type { Period } from "../../funnel-detail/types";
import { getCompanyFinanceSummary } from "../../../services/financeService";
import { parseChartDate, resolvePeriodDayDate } from "./chartTime";
import { formatCurrency } from "./utils";
import type { CompanyDailyPoint } from "./types";

type Props = {
  period: Period;
  dailyData: CompanyDailyPoint[];
  costBreakdown?: {
    totalProductCosts: number;
    totalShippingCosts: number;
    totalLogisticsCosts: number;
    totalVariableCosts?: number;
  };
};

type ChartMode = "cash" | "cumulative";
type CostSegmentKey = "productCost" | "logisticsCost" | "adCost";

type CashFlowDay = {
  date: Date;
  revenue: number;
  productCost: number;
  logisticsCost: number;
  adCost: number;
};

type VisibleChartPeriod = Period & {
  lockedToLast15: boolean;
};

type CashFlowPoint = {
  label: string;
  tooltipLabel: string;
  revenue: number;
  productCost: number;
  logisticsCost: number;
  adCost: number;
  costs: number;
  profit: number;
  cumulativeRevenue: number;
  cumulativeCosts: number;
  cumulativeProfit: number;
  margin: number;
};

type ChartPoint = CashFlowPoint & {
  x: number;
};

type YTick = {
  value: number;
  y: number;
};

const CHART_WIDTH = 1000;
const CHART_HEIGHT = 350;
const CHART_LEFT = 74;
const CHART_RIGHT = 30;
const CHART_TOP = 28;
const CHART_BOTTOM = 48;
const PLOT_WIDTH = CHART_WIDTH - CHART_LEFT - CHART_RIGHT;
const PLOT_HEIGHT = CHART_HEIGHT - CHART_TOP - CHART_BOTTOM;
const VISIBLE_PERIODS = 15;
const SHORT_MONTHS = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

const COST_SEGMENTS: { key: CostSegmentKey; label: string; color: string }[] = [
  { key: "productCost", label: "Producto", color: "#64748b" },
  { key: "logisticsCost", label: "Logística", color: "#0ea5e9" },
  { key: "adCost", label: "Publicidad", color: "#f59e0b" },
];

export function CashFlowChart({ period, dailyData, costBreakdown }: Props) {
  const [mode, setMode] = useState<ChartMode>("cash");
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [expandedRange, setExpandedRange] = useState<{ dailyData: CompanyDailyPoint[]; costBreakdown?: Props["costBreakdown"] } | null>(null);
  const chartPeriod = useMemo(() => resolveVisibleChartPeriod(period), [period]);
  const chartDailyData = expandedRange?.dailyData ?? dailyData;
  const chartCostBreakdown = expandedRange?.costBreakdown ?? costBreakdown;
  const cashFlowRows = useMemo(() => resolveCashFlowRows(chartPeriod, chartDailyData, chartCostBreakdown), [chartCostBreakdown, chartDailyData, chartPeriod]);
  const excelRows = useMemo(() => resolveExcelCashFlowRows(period, dailyData, costBreakdown), [costBreakdown, dailyData, period]);
  const bucketedData = useMemo(() => buildCashFlowBuckets(chartPeriod, cashFlowRows), [chartPeriod, cashFlowRows]);
  const totals = useMemo(() => buildTotals(cashFlowRows), [cashFlowRows]);
  const cashModel = useMemo(() => buildCashStructureModel(bucketedData), [bucketedData]);
  const cumulativeModel = useMemo(() => buildCumulativeModel(bucketedData), [bucketedData]);
  const activeModel = mode === "cash" ? cashModel : cumulativeModel;

  useEffect(() => {
    let cancelled = false;

    if (!chartPeriod.lockedToLast15) {
      setExpandedRange(null);
      return;
    }

    getCompanyFinanceSummary(chartPeriod)
      .then((summary) => {
        if (cancelled) return;
        setExpandedRange({
          dailyData: summary.dailyData,
          costBreakdown: {
            totalProductCosts: summary.totalProductCosts,
            totalShippingCosts: summary.totalShippingCosts,
            totalLogisticsCosts: summary.totalLogisticsCosts,
            totalVariableCosts: summary.totalVariableCosts,
          },
        });
      })
      .catch(() => {
        if (!cancelled) setExpandedRange(null);
      });

    return () => {
      cancelled = true;
    };
  }, [chartPeriod]);

  return (
    <section id="finance-company-cashflow-target" className="overflow-hidden rounded-lg border border-gray-200 bg-white tabular-nums shadow-sm">
      <header className="flex flex-col gap-3 border-b border-gray-100 px-5 py-4 xl:flex-row xl:items-center xl:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="text-base font-semibold text-gray-900">Flujo de caja</h2>
            <div className="inline-flex w-fit rounded-lg bg-gray-100 p-1 text-xs font-semibold">
              <ModeButton active={mode === "cash"} onClick={() => setMode("cash")}>
                Recaudo vs costos
              </ModeButton>
              <ModeButton active={mode === "cumulative"} onClick={() => setMode("cumulative")}>
                Acumulado
              </ModeButton>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-1.5 xl:justify-end">
          <button
            type="button"
            onClick={() => downloadCashFlowExcel(period, excelRows)}
            aria-label="Descargar flujo de caja en Excel"
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 shadow-sm transition hover:bg-gray-50 hover:text-gray-800"
            title="Descargar flujo de caja en Excel"
          >
            <Download className="h-4 w-4" />
          </button>
          <div className="grid grid-cols-3 gap-1.5 text-right">
            <SummaryPill label="Recaudo" value={totals.revenue} tone="success" />
            <SummaryPill label="Costos" value={totals.costs} tone="neutral" />
            <SummaryPill label="Utilidad" value={totals.profit} tone={totals.profit >= 0 ? "success" : "danger"} signed />
          </div>
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-4 px-5 pt-3 text-xs font-semibold">
        {mode === "cash" ? (
          <>
            <LegendBlock color="#10b981" label="Recaudo" />
            {COST_SEGMENTS.map((segment) => (
              <LegendBlock key={segment.key} color={segment.color} label={segment.label} />
            ))}
          </>
        ) : (
          <>
            <LegendLine color="#10b981" label="Recaudo acumulado" />
            <LegendLine color="#64748b" label="Costos acumulados" />
            <LegendLine color="#2b5ce6" label="Utilidad acumulada" />
          </>
        )}
      </div>

      <div className="h-[390px] overflow-x-auto px-3 pb-5 pt-1">
        {mode === "cash" ? (
          <CashStructureSvg
            model={cashModel}
            hoveredIndex={hoveredIndex}
            onHover={setHoveredIndex}
            onLeave={() => setHoveredIndex(null)}
          />
        ) : (
          <CumulativeSvg model={cumulativeModel} hoveredIndex={hoveredIndex} onHover={setHoveredIndex} onLeave={() => setHoveredIndex(null)} />
        )}
      </div>
      <span className="sr-only">{activeModel.points.length} puntos visibles</span>
    </section>
  );
}

function ModeButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={
        active
          ? "rounded-md bg-white px-3 py-1.5 text-gray-900 shadow-sm transition"
          : "rounded-md px-3 py-1.5 text-gray-500 transition hover:text-gray-800"
      }
    >
      {children}
    </button>
  );
}

function CashStructureSvg({
  model,
  hoveredIndex,
  onHover,
  onLeave,
}: {
  model: ReturnType<typeof buildCashStructureModel>;
  hoveredIndex: number | null;
  onHover: (index: number) => void;
  onLeave: () => void;
}) {
  const hoveredPoint = hoveredIndex === null ? null : model.points[hoveredIndex] ?? null;
  const baselineY = CHART_TOP + PLOT_HEIGHT;

  return (
    <svg
      className="h-full w-full min-w-[860px] overflow-visible"
      viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
      role="img"
      aria-label="Recaudo y costos por día"
      onMouseLeave={onLeave}
    >
      <rect x={CHART_LEFT} y={CHART_TOP} width={PLOT_WIDTH} height={PLOT_HEIGHT} fill="#fff" />
      <GridAndAxes yTicks={model.yTicks} xLabels={model.xLabels} />

      {model.points.map((point, index) => {
        const revenueY = model.scaleY(point.revenue);
        const revenueHeight = Math.max(baselineY - revenueY, point.revenue > 0 ? 2 : 0);
        const hovered = hoveredIndex === index;
        const revenueX = point.x - model.barWidth - model.barGap / 2;
        const costX = point.x + model.barGap / 2;
        let costBase = 0;

        return (
          <g key={`${point.label}-${index}`}>
            <rect
              x={revenueX}
              y={revenueY}
              width={model.barWidth}
              height={revenueHeight}
              rx={6}
              fill="#10b981"
              fillOpacity={hovered ? 0.96 : 0.86}
            />

            {COST_SEGMENTS.map((segment) => {
              const value = point[segment.key];
              if (value <= 0) return null;
              const y = model.scaleY(costBase + value);
              const height = Math.max(model.scaleY(costBase) - y, 1);
              costBase += value;

              return (
                <rect
                  key={segment.key}
                  x={costX}
                  y={y}
                  width={model.barWidth}
                  height={height}
                  rx={3}
                  fill={segment.color}
                  fillOpacity={hovered ? 0.98 : 0.86}
                  stroke="#fff"
                  strokeWidth="1"
                />
              );
            })}

            <rect
              x={point.x - model.hitWidth / 2}
              y={CHART_TOP}
              width={model.hitWidth}
              height={PLOT_HEIGHT}
              fill="transparent"
              onMouseEnter={() => onHover(index)}
            />
          </g>
        );
      })}

      {hoveredPoint ? <CashStructureTooltip point={hoveredPoint} x={hoveredPoint.x} scaleY={model.scaleY} /> : null}
    </svg>
  );
}

function CumulativeSvg({
  model,
  hoveredIndex,
  onHover,
  onLeave,
}: {
  model: ReturnType<typeof buildCumulativeModel>;
  hoveredIndex: number | null;
  onHover: (index: number) => void;
  onLeave: () => void;
}) {
  const hoveredPoint = hoveredIndex === null ? null : model.points[hoveredIndex] ?? null;

  return (
    <svg
      className="h-full w-full min-w-[860px] overflow-visible"
      viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
      role="img"
      aria-label="Recaudo costos y utilidad acumulada"
      onMouseLeave={onLeave}
    >
      <defs>
        <linearGradient id="cashflow-revenue-area" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#10b981" stopOpacity="0.14" />
          <stop offset="100%" stopColor="#10b981" stopOpacity="0.02" />
        </linearGradient>
        <linearGradient id="cashflow-costs-area" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#64748b" stopOpacity="0.12" />
          <stop offset="100%" stopColor="#64748b" stopOpacity="0.02" />
        </linearGradient>
        <linearGradient id="cashflow-profit-area" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#2b5ce6" stopOpacity="0.15" />
          <stop offset="100%" stopColor="#2b5ce6" stopOpacity="0.02" />
        </linearGradient>
      </defs>

      <rect x={CHART_LEFT} y={CHART_TOP} width={PLOT_WIDTH} height={PLOT_HEIGHT} fill="#fff" />

      {model.yTicks.map((tick) => (
        <g key={tick.value}>
          <line
            x1={CHART_LEFT}
            x2={CHART_WIDTH - CHART_RIGHT}
            y1={tick.y}
            y2={tick.y}
            stroke={tick.value === 0 ? "#cbd5e1" : "#eef2f7"}
            strokeDasharray={tick.value === 0 ? "0" : "4 6"}
          />
          <text x={CHART_LEFT - 12} y={tick.y + 4} textAnchor="end" fontSize="12" fill="#6b7280">
            {formatChartMoneyCompact(tick.value)}
          </text>
        </g>
      ))}

      <path d={model.revenueAreaPath} fill="url(#cashflow-revenue-area)" />
      <path d={model.costsAreaPath} fill="url(#cashflow-costs-area)" />
      <path d={model.profitAreaPath} fill="url(#cashflow-profit-area)" />

      <polyline
        points={model.revenuePolyline}
        fill="none"
        stroke="#10b981"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <polyline
        points={model.costsPolyline}
        fill="none"
        stroke="#64748b"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <polyline
        points={model.profitPolyline}
        fill="none"
        stroke="#2b5ce6"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {model.points.map((point, index) => (
        <g key={`${point.label}-${index}-hit`}>
          <rect
            x={point.x - model.hitWidth / 2}
            y={CHART_TOP}
            width={model.hitWidth}
            height={PLOT_HEIGHT}
            fill="transparent"
            onMouseEnter={() => onHover(index)}
          />
          <circle cx={point.x} cy={model.scaleY(point.cumulativeRevenue)} r={hoveredIndex === index ? 4 : 3} fill="#10b981" stroke="#fff" strokeWidth="1.5" />
          <circle cx={point.x} cy={model.scaleY(point.cumulativeCosts)} r={hoveredIndex === index ? 4 : 3} fill="#64748b" stroke="#fff" strokeWidth="1.5" />
          <circle cx={point.x} cy={model.scaleY(point.cumulativeProfit)} r={hoveredIndex === index ? 4 : 3} fill="#2b5ce6" stroke="#fff" strokeWidth="1.5" />
        </g>
      ))}

      {model.xLabels.map((label) => (
        <text key={`${label.x}-${label.text}`} x={label.x} y={CHART_HEIGHT - 16} textAnchor="middle" fontSize="12" fill="#6b7280">
          {label.text}
        </text>
      ))}

      <line
        x1={CHART_LEFT}
        x2={CHART_WIDTH - CHART_RIGHT}
        y1={CHART_TOP + PLOT_HEIGHT}
        y2={CHART_TOP + PLOT_HEIGHT}
        stroke="#e5e7eb"
      />

      {hoveredPoint ? <CumulativeTooltip point={hoveredPoint} x={hoveredPoint.x} y={model.scaleY(hoveredPoint.cumulativeProfit)} /> : null}
    </svg>
  );
}

function GridAndAxes({ yTicks, xLabels }: { yTicks: YTick[]; xLabels: { x: number; text: string }[] }) {
  return (
    <>
      {yTicks.map((tick) => (
        <g key={tick.value}>
          <line
            x1={CHART_LEFT}
            x2={CHART_WIDTH - CHART_RIGHT}
            y1={tick.y}
            y2={tick.y}
            stroke="#eef2f7"
            strokeDasharray={tick.value === 0 ? "0" : "4 6"}
          />
          <text x={CHART_LEFT - 12} y={tick.y + 4} textAnchor="end" fontSize="12" fill="#6b7280">
            {formatChartMoneyCompact(tick.value)}
          </text>
        </g>
      ))}

      {xLabels.map((label) => (
        <text key={`${label.x}-${label.text}`} x={label.x} y={CHART_HEIGHT - 16} textAnchor="middle" fontSize="12" fill="#6b7280">
          {label.text}
        </text>
      ))}

      <line
        x1={CHART_LEFT}
        x2={CHART_WIDTH - CHART_RIGHT}
        y1={CHART_TOP + PLOT_HEIGHT}
        y2={CHART_TOP + PLOT_HEIGHT}
        stroke="#e5e7eb"
      />
    </>
  );
}

function CashStructureTooltip({
  point,
  x,
  scaleY,
}: {
  point: ChartPoint;
  x: number;
  scaleY: (value: number) => number;
}) {
  const tooltipWidth = 218;
  const tooltipHeight = 130;
  const tooltipX = x > CHART_WIDTH - CHART_RIGHT - tooltipWidth ? x - tooltipWidth - 14 : x + 14;
  const tooltipY = Math.max(CHART_TOP + 4, Math.min(scaleY(Math.max(point.revenue, point.costs)) - 18, CHART_TOP + PLOT_HEIGHT - tooltipHeight));
  const profitColor = point.profit >= 0 ? "#047857" : "#b91c1c";

  return (
    <g pointerEvents="none">
      <line x1={x} x2={x} y1={CHART_TOP} y2={CHART_TOP + PLOT_HEIGHT} stroke="#dbeafe" strokeWidth="1.5" />
      <rect x={tooltipX} y={tooltipY} width={tooltipWidth} height={tooltipHeight} rx={10} fill="#fff" stroke="#e5e7eb" />
      <text x={tooltipX + 12} y={tooltipY + 21} fontSize="12" fontWeight="700" fill="#111827">
        {point.tooltipLabel || point.label}
      </text>
      <TooltipRow x={tooltipX} y={tooltipY + 42} label="Recaudo" value={formatChartMoneyCompact(point.revenue)} color="#047857" />
      <TooltipRow x={tooltipX} y={tooltipY + 59} label="Producto" value={formatChartMoneyCompact(point.productCost)} color="#475569" />
      <TooltipRow x={tooltipX} y={tooltipY + 76} label="Logística" value={formatChartMoneyCompact(point.logisticsCost)} color="#0284c7" />
      <TooltipRow x={tooltipX} y={tooltipY + 93} label="Publicidad" value={formatChartMoneyCompact(point.adCost)} color="#b45309" />
      <line x1={tooltipX + 12} x2={tooltipX + tooltipWidth - 12} y1={tooltipY + 104} y2={tooltipY + 104} stroke="#e5e7eb" />
      <TooltipRow x={tooltipX} y={tooltipY + 121} label="Utilidad" value={formatSignedCompactCurrency(point.profit)} color={profitColor} strong />
    </g>
  );
}

function CumulativeTooltip({ point, x, y }: { point: ChartPoint; x: number; y: number }) {
  const tooltipWidth = 228;
  const tooltipHeight = 112;
  const tooltipX = x > CHART_WIDTH - CHART_RIGHT - tooltipWidth ? x - tooltipWidth - 14 : x + 14;
  const tooltipY = Math.max(CHART_TOP + 4, Math.min(y - 18, CHART_TOP + PLOT_HEIGHT - tooltipHeight));
  const profitColor = point.cumulativeProfit >= 0 ? "#1d4ed8" : "#b91c1c";

  return (
    <g pointerEvents="none">
      <line x1={x} x2={x} y1={CHART_TOP} y2={CHART_TOP + PLOT_HEIGHT} stroke="#dbeafe" strokeWidth="1.5" />
      <rect x={tooltipX} y={tooltipY} width={tooltipWidth} height={tooltipHeight} rx={10} fill="#fff" stroke="#e5e7eb" />
      <text x={tooltipX + 12} y={tooltipY + 21} fontSize="12" fontWeight="700" fill="#111827">
        {point.tooltipLabel || point.label}
      </text>
      <TooltipRow x={tooltipX} y={tooltipY + 44} label="Recaudo acum." value={formatChartMoneyCompact(point.cumulativeRevenue)} color="#047857" />
      <TooltipRow x={tooltipX} y={tooltipY + 63} label="Costos acum." value={formatChartMoneyCompact(point.cumulativeCosts)} color="#475569" />
      <TooltipRow x={tooltipX} y={tooltipY + 82} label="Utilidad acum." value={formatSignedCompactCurrency(point.cumulativeProfit)} color={profitColor} strong />
      <TooltipRow x={tooltipX} y={tooltipY + 101} label="Margen acum." value={`${point.margin.toFixed(1)}%`} color="#475569" />
    </g>
  );
}

function TooltipRow({
  x,
  y,
  label,
  value,
  color,
  strong = false,
}: {
  x: number;
  y: number;
  label: string;
  value: string;
  color: string;
  strong?: boolean;
}) {
  return (
    <>
      <text x={x + 12} y={y} fontSize="11" fontWeight={strong ? "700" : "500"} fill={color}>
        {label}
      </text>
      <text x={x + 206} y={y} textAnchor="end" fontSize="11" fontWeight="700" fill={color}>
        {value}
      </text>
    </>
  );
}

function resolveVisibleChartPeriod(period: Period): VisibleChartPeriod {
  const to = startOfDay(period.to);
  const shouldRespectFilter = Math.max(1, period.days || countInclusiveDays(period.from, period.to)) >= VISIBLE_PERIODS;
  const from = shouldRespectFilter ? startOfDay(period.from) : addDays(to, -(VISIBLE_PERIODS - 1));
  const days = countInclusiveDays(from, to);

  return {
    ...period,
    from,
    to,
    days,
    lockedToLast15: !shouldRespectFilter,
  };
}

function resolveCashFlowRows(period: VisibleChartPeriod, dailyData: CompanyDailyPoint[], costBreakdown?: Props["costBreakdown"]) {
  return buildDailyRows(period, mapCompanyDailyRows(period, dailyData, costBreakdown));
}

function resolveExcelCashFlowRows(period: Period, dailyData: CompanyDailyPoint[], costBreakdown?: Props["costBreakdown"]) {
  const exportPeriod: VisibleChartPeriod = {
    ...period,
    from: startOfDay(period.from),
    to: startOfDay(period.to),
    days: countInclusiveDays(period.from, period.to),
    lockedToLast15: false,
  };
  return buildDailyRows(exportPeriod, mapCompanyDailyRows(exportPeriod, dailyData, costBreakdown));
}

function mapCompanyDailyRows(period: Period, dailyData: CompanyDailyPoint[], costBreakdown?: Props["costBreakdown"]) {
  const variableTotal = Math.max(
    0,
    Number(
      costBreakdown?.totalVariableCosts ??
        Number(costBreakdown?.totalProductCosts ?? 0) + Number(costBreakdown?.totalLogisticsCosts ?? 0)
    )
  );
  const productRatio = variableTotal > 0 ? clamp(Number(costBreakdown?.totalProductCosts ?? 0) / variableTotal, 0, 1) : 0;

  return dailyData.map((row, index) => {
    const date = parseChartDate(row.date) ?? resolvePeriodDayDate(period, row.day, index);
    const recognizedVariableCost = Math.max(0, Number(row.logisticsCosts ?? 0));
    const productCost = recognizedVariableCost * productRatio;
    const logisticsCost = Math.max(0, recognizedVariableCost - productCost);
    const adCost = Math.max(0, Number(row.adInvestment ?? 0));
    const declaredCosts = Math.max(0, Number(row.costs ?? 0));
    const unallocatedCost = Math.max(0, declaredCosts - recognizedVariableCost - adCost);

    return {
      date,
      revenue: Math.max(0, Number(row.revenue ?? 0)),
      productCost: productCost + unallocatedCost,
      logisticsCost,
      adCost,
    };
  });
}

function buildDailyRows(period: VisibleChartPeriod, rows: CashFlowDay[]) {
  const rowsByDate = new Map<string, CashFlowDay>();

  rows.forEach((row) => {
    const key = toDateKey(row.date);
    const existing = rowsByDate.get(key) ?? {
      date: startOfDay(row.date),
      revenue: 0,
      productCost: 0,
      logisticsCost: 0,
      adCost: 0,
    };

    rowsByDate.set(key, {
      date: existing.date,
      revenue: existing.revenue + row.revenue,
      productCost: existing.productCost + row.productCost,
      logisticsCost: existing.logisticsCost + row.logisticsCost,
      adCost: existing.adCost + row.adCost,
    });
  });

  const dailyRows: CashFlowDay[] = [];
  for (let date = startOfDay(period.from); date <= startOfDay(period.to); date = addDays(date, 1)) {
    dailyRows.push(
      rowsByDate.get(toDateKey(date)) ?? {
        date,
        revenue: 0,
        productCost: 0,
        logisticsCost: 0,
        adCost: 0,
      }
    );
  }

  return dailyRows;
}

function buildFixedTimeBuckets(period: VisibleChartPeriod, rows: CashFlowDay[]) {
  const from = startOfDay(period.from);
  const to = startOfDay(period.to);
  const totalDays = Math.max(VISIBLE_PERIODS, countInclusiveDays(from, to));
  const rowsByDate = new Map<string, CashFlowDay[]>();

  rows.forEach((row) => {
    const date = startOfDay(row.date);
    if (date < from || date > to) return;
    const key = toDateKey(date);
    const existingRows = rowsByDate.get(key) ?? [];
    existingRows.push(row);
    rowsByDate.set(key, existingRows);
  });

  return Array.from({ length: VISIBLE_PERIODS }, (_, index) => {
    const startOffset = Math.floor((index * totalDays) / VISIBLE_PERIODS);
    const endOffset = Math.max(startOffset, Math.floor(((index + 1) * totalDays) / VISIBLE_PERIODS) - 1);
    const startDate = addDays(from, startOffset);
    const endDate = new Date(Math.min(addDays(from, endOffset).getTime(), to.getTime()));
    const bucketRows: CashFlowDay[] = [];

    for (let date = startDate; date <= endDate; date = addDays(date, 1)) {
      bucketRows.push(...(rowsByDate.get(toDateKey(date)) ?? []));
    }

    return {
      label: formatBucketLabel(startDate),
      tooltipLabel: formatBucketTooltipLabel(startDate, endDate),
      rows: bucketRows,
    };
  });
}

function buildCashFlowBuckets(period: VisibleChartPeriod, rows: CashFlowDay[]): CashFlowPoint[] {
  const buckets = buildFixedTimeBuckets(period, rows);
  let cumulativeProfit = 0;
  let cumulativeRevenue = 0;
  let cumulativeCosts = 0;

  return buckets.map((bucket) => {
    const revenue = bucket.rows.reduce((sum, row) => sum + row.revenue, 0);
    const productCost = bucket.rows.reduce((sum, row) => sum + row.productCost, 0);
    const logisticsCost = bucket.rows.reduce((sum, row) => sum + row.logisticsCost, 0);
    const adCost = bucket.rows.reduce((sum, row) => sum + row.adCost, 0);
    const costs = productCost + logisticsCost + adCost;
    const profit = revenue - costs;
    cumulativeRevenue += revenue;
    cumulativeCosts += costs;
    cumulativeProfit += profit;

    return {
      label: bucket.label,
      tooltipLabel: bucket.tooltipLabel,
      revenue,
      productCost,
      logisticsCost,
      adCost,
      costs,
      profit,
      cumulativeRevenue,
      cumulativeCosts,
      cumulativeProfit,
      margin: cumulativeRevenue > 0 ? (cumulativeProfit / cumulativeRevenue) * 100 : 0,
    };
  });
}

function buildTotals(rows: CashFlowDay[]) {
  const totals = rows.reduce(
    (acc, row) => {
      const costs = row.productCost + row.logisticsCost + row.adCost;
      acc.revenue += row.revenue;
      acc.productCost += row.productCost;
      acc.logisticsCost += row.logisticsCost;
      acc.adCost += row.adCost;
      acc.costs += costs;
      if (row.revenue !== 0 || costs !== 0) {
        if (row.revenue >= costs) acc.profitableDays += 1;
        else acc.lossDays += 1;
      }
      return acc;
    },
    { revenue: 0, productCost: 0, logisticsCost: 0, adCost: 0, costs: 0, profitableDays: 0, lossDays: 0 }
  );

  const profit = totals.revenue - totals.costs;
  return {
    ...totals,
    profit,
    margin: totals.revenue > 0 ? (profit / totals.revenue) * 100 : 0,
  };
}

function buildCashStructureModel(points: CashFlowPoint[]) {
  const maxValue = Math.max(1, ...points.flatMap((point) => [point.revenue, point.costs]));
  const yMax = maxValue * 1.14;
  const scaleY = (value: number) => CHART_TOP + (1 - value / yMax) * PLOT_HEIGHT;
  const hydratedPoints = hydratePoints(points);
  const yTicks = buildLinearTicks(0, yMax, scaleY);
  const barWidth = Math.max(14, Math.min(22, PLOT_WIDTH / Math.max(points.length, 1) * 0.34));
  const barGap = 2;

  return {
    points: hydratedPoints,
    yTicks,
    xLabels: buildXLabels(hydratedPoints),
    scaleY,
    barWidth,
    barGap,
    hitWidth: Math.max(20, PLOT_WIDTH / Math.max(points.length, 1) * 0.9),
  };
}

function buildCumulativeModel(points: CashFlowPoint[]) {
  const hydratedPoints = hydratePoints(points);
  const values = hydratedPoints.flatMap((point) => [point.cumulativeRevenue, point.cumulativeCosts, point.cumulativeProfit, 0]);
  const scaleY = buildPerspectiveScale(values);
  const zeroY = scaleY(0);
  const revenuePolyline = hydratedPoints.map((point) => `${point.x},${scaleY(point.cumulativeRevenue)}`).join(" ");
  const costsPolyline = hydratedPoints.map((point) => `${point.x},${scaleY(point.cumulativeCosts)}`).join(" ");
  const profitPolyline = hydratedPoints.map((point) => `${point.x},${scaleY(point.cumulativeProfit)}`).join(" ");
  const revenueAreaPath = buildLineAreaPath(hydratedPoints, scaleY, zeroY, (point) => point.cumulativeRevenue);
  const costsAreaPath = buildLineAreaPath(hydratedPoints, scaleY, zeroY, (point) => point.cumulativeCosts);
  const profitAreaPath = buildLineAreaPath(hydratedPoints, scaleY, zeroY, (point) => point.cumulativeProfit);
  return {
    points: hydratedPoints,
    yTicks: buildPerspectiveTicks(values, scaleY),
    xLabels: buildXLabels(hydratedPoints),
    scaleY,
    zeroY,
    revenuePolyline,
    costsPolyline,
    profitPolyline,
    revenueAreaPath,
    costsAreaPath,
    profitAreaPath,
    hitWidth: Math.max(20, PLOT_WIDTH / Math.max(points.length, 1) * 0.9),
  };
}

function buildPerspectiveScale(values: number[]) {
  const finiteValues = values.filter((value) => Number.isFinite(value));
  const minValue = Math.min(0, ...finiteValues);
  const maxValue = Math.max(0, ...finiteValues, 1);
  const maxAbs = Math.max(Math.abs(minValue), Math.abs(maxValue), 1);
  const lens = Math.max(maxAbs * 0.08, 1);
  const transform = (value: number) => Math.sign(value) * Math.log1p(Math.abs(value) / lens);
  const transformedMin = transform(minValue);
  const transformedMax = transform(maxValue);
  const transformedSpan = Math.max(transformedMax - transformedMin, 0.001);

  return (value: number) => CHART_TOP + (1 - (transform(value) - transformedMin) / transformedSpan) * PLOT_HEIGHT;
}

function buildPerspectiveTicks(values: number[], scaleY: (value: number) => number): YTick[] {
  const finiteValues = values.filter((value) => Number.isFinite(value));
  const minValue = Math.min(0, ...finiteValues);
  const maxValue = Math.max(0, ...finiteValues, 1);
  const positiveTicks = [maxValue, maxValue * 0.5, 0];
  const negativeTicks = minValue < 0 ? [minValue * 0.5, minValue] : [];

  return [...positiveTicks, ...negativeTicks]
    .filter((value, index, list) => list.findIndex((candidate) => Math.abs(candidate - value) < 1) === index)
    .map((value) => ({ value, y: scaleY(value) }))
    .sort((a, b) => a.y - b.y);
}

function hydratePoints(points: CashFlowPoint[]): ChartPoint[] {
  const step = points.length > 1 ? PLOT_WIDTH / (points.length - 1) : PLOT_WIDTH;

  return points.map((point, index) => ({
    ...point,
    x: CHART_LEFT + (points.length > 1 ? index * step : PLOT_WIDTH / 2),
  }));
}

function buildXLabels(points: ChartPoint[]) {
  return points.map((point) => ({ x: point.x, text: point.label }));
}

function buildLinearTicks(minY: number, maxY: number, scaleY: (value: number) => number): YTick[] {
  const span = Math.max(maxY - minY, 1);

  return Array.from({ length: 4 }, (_, index) => {
    const value = minY + (span * index) / 3;
    return { value, y: scaleY(value) };
  }).reverse();
}

function buildLineAreaPath(points: ChartPoint[], scaleY: (value: number) => number, baselineY: number, getValue: (point: ChartPoint) => number) {
  if (points.length === 0) return "";

  const first = points[0];
  const last = points[points.length - 1];
  const line = points.map((point) => `L ${point.x} ${scaleY(getValue(point))}`).join(" ");
  return `M ${first.x} ${baselineY} ${line} L ${last.x} ${baselineY} Z`;
}

function startOfDay(value: Date) {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
}

function addDays(value: Date, days: number) {
  const date = new Date(value);
  date.setDate(date.getDate() + days);
  return startOfDay(date);
}

function countInclusiveDays(from: Date, to: Date) {
  const start = startOfDay(from).getTime();
  const end = startOfDay(to).getTime();
  return Math.max(1, Math.round((end - start) / 86_400_000) + 1);
}

function toDateKey(value: Date) {
  const date = startOfDay(value);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function formatShortDate(date: Date) {
  return `${date.getDate()} ${SHORT_MONTHS[date.getMonth()]}`;
}

function formatBucketLabel(startDate: Date) {
  return formatShortDate(startDate);
}

function formatBucketTooltipLabel(startDate: Date, endDate: Date) {
  if (toDateKey(startDate) === toDateKey(endDate)) return formatShortDate(startDate);

  const sameMonth = startDate.getMonth() === endDate.getMonth() && startDate.getFullYear() === endDate.getFullYear();
  if (sameMonth) return `${startDate.getDate()}-${endDate.getDate()} ${SHORT_MONTHS[endDate.getMonth()]}`;

  return `${formatShortDate(startDate)} - ${formatShortDate(endDate)}`;
}

function downloadCashFlowExcel(period: Period, rows: CashFlowDay[]) {
  const filename = `flujo-de-caja-${toDateKey(period.from)}-${toDateKey(period.to)}.xls`;
  const columns = rows.map((row) => formatShortDate(row.date));
  const conceptRows = [
    { label: "Recaudo", values: rows.map((row) => row.revenue), bold: true },
    { label: "Costos", values: rows.map((row) => row.productCost + row.logisticsCost + row.adCost), bold: true },
    { label: "Costo del producto", values: rows.map((row) => row.productCost) },
    { label: "Costo logístico", values: rows.map((row) => row.logisticsCost) },
    { label: "Inversión publicitaria", values: rows.map((row) => row.adCost) },
    {
      label: "Utilidad",
      values: rows.map((row) => row.revenue - row.productCost - row.logisticsCost - row.adCost),
      bold: true,
    },
  ];

  const header = ["Concepto", ...columns].map((value) => `<th>${escapeHtml(value)}</th>`).join("");
  const body = conceptRows
    .map((row) => {
      const cells = row.values
        .map((value) => `<td style='mso-number-format:"$ #,##0";'>${Math.round(value)}</td>`)
        .join("");
      return `<tr${row.bold ? " style='font-weight:700;'" : ""}><td>${escapeHtml(row.label)}</td>${cells}</tr>`;
    })
    .join("");
  const html = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <style>
      table { border-collapse: collapse; font-family: Arial, sans-serif; font-size: 12px; }
      th, td { border: 1px solid #d9e2ec; padding: 6px 8px; white-space: nowrap; }
      th { background: #eef2ff; color: #15124f; font-weight: 700; }
      td:first-child { background: #f8fafc; color: #111827; font-weight: 600; }
    </style>
  </head>
  <body>
    <table>
      <thead><tr>${header}</tr></thead>
      <tbody>${body}</tbody>
    </table>
  </body>
</html>`;

  const blob = new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function escapeHtml(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function SummaryPill({
  label,
  value,
  tone,
  signed = false,
}: {
  label: string;
  value: number;
  tone: "success" | "neutral" | "danger";
  signed?: boolean;
}) {
  const toneClasses = {
    success: "border-emerald-100 bg-emerald-50 text-emerald-900",
    neutral: "border-gray-200 bg-gray-50 text-gray-900",
    danger: "border-red-100 bg-red-50 text-red-900",
  };

  return (
    <div className={`min-w-[92px] rounded-lg border px-2.5 py-2 ${toneClasses[tone]}`} title={formatCurrency(value)}>
      <p className="text-[9px] font-semibold uppercase tracking-wide opacity-70">{label}</p>
      <p className="mt-0.5 text-sm font-bold">{signed ? formatSignedCompactCurrency(value) : formatChartMoneyCompact(value)}</p>
    </div>
  );
}

function LegendBlock({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-2 text-gray-500">
      <span className="h-3 w-5 rounded-[4px]" style={{ backgroundColor: color }} />
      {label}
    </span>
  );
}

function LegendLine({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-2 text-gray-500">
      <span className="h-[3px] w-6 rounded-full" style={{ backgroundColor: color }} />
      {label}
    </span>
  );
}

function formatSignedCompactCurrency(value: number) {
  const sign = value >= 0 ? "+" : "-";
  return `${sign}${formatChartMoneyCompact(Math.abs(value))}`;
}

function formatChartMoneyCompact(value: number) {
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  if (abs >= 1_000_000_000) return `${sign}$${(abs / 1_000_000_000).toFixed(1)}B`;
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(abs >= 100_000_000 ? 0 : 1)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(0)}K`;
  return `${sign}$${abs.toFixed(0)}`;
}
